<template>
    <v-dialog 
        :value="value" 
        @input="$emit('input', $event)" 
        max-width="480" 
        persistent
        content-class="feedback-dialog"
    >
        <v-card class="feedback-card">
            <!-- Header with close button -->
            <div class="feedback-header">
                <v-btn 
                    icon 
                    small
                    class="close-btn"
                    @click="skipFeedback"
                >
                    <v-icon size="20">mdi-close</v-icon>
                </v-btn>
            </div>

            <v-card-text class="feedback-content text-center">
                <h3 class="feedback-title">
                    How was your session with {{ teacherName }}?
                </h3>
                <p class="feedback-subtitle">
                    Your feedback helps us improve your learning experience.
                </p>

                <!-- Star Rating -->
                <div class="rating-container">
                    <v-btn 
                        v-for="star in 5" 
                        :key="star"
                        icon 
                        large
                        @click="rating = star"
                        class="star-btn"
                    >
                        <v-icon 
                            size="36" 
                            :color="isStarFilled(star) ? '#F5A623' : '#E0E0E0'"
                        >
                            {{ isStarFilled(star) ? 'mdi-star' : 'mdi-star-outline' }}
                        </v-icon>
                    </v-btn>
                </div>

                <!-- Comment -->
                <div class="comment-section">
                    <label class="comment-label">
                        {{ isCommentRequired ? 'Please tell us what went wrong' : 'Add a comment (optional)' }}
                        <span v-if="isCommentRequired" class="required-asterisk">*</span>
                    </label>
                    <v-textarea
                        v-model="comment"
                        outlined
                        :placeholder="isCommentRequired ? 'Help us understand how we can improve' : 'What did you like or what can be improved?'"
                        rows="3"
                        :hide-details="!showCommentError"
                        :error="showCommentError"
                        :error-messages="showCommentError ? 'Please provide a comment when rating below 4 stars' : ''"
                        class="comment-input"
                    ></v-textarea>
                </div>

                <!-- Actions -->
                <div class="feedback-actions">
                    <v-btn 
                        text 
                        class="skip-btn"
                        @click="skipFeedback"
                        :disabled="submitting"
                    >
                        Skip
                    </v-btn>
                    <v-btn
                        color="primary"
                        depressed
                        class="submit-btn"
                        @click="submitFeedback"
                        :loading="submitting"
                        :disabled="!canSubmit"
                    >
                        Submit Feedback
                    </v-btn>
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'StudentFeedbackDialog',
    props: {
        value: {
            type: Boolean,
            default: false
        },
        pendingFeedback: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            rating: 0,
            comment: '',
            submitting: false,
            commentTouched: false
        };
    },
    computed: {
        teacherName() {
            return this.pendingFeedback ? this.pendingFeedback.teacher_name : 'Your Teacher';
        },
        classId() {
            return this.pendingFeedback ? this.pendingFeedback.class_id : null;
        },
        isCommentRequired() {
            return this.rating > 0 && this.rating < 4;
        },
        isCommentMissing() {
            return this.isCommentRequired && !this.comment.trim();
        },
        showCommentError() {
            return this.commentTouched && this.isCommentMissing;
        },
        canSubmit() {
            return this.rating > 0 && !this.isCommentMissing;
        }
    },
    watch: {
        value(show) {
            if (show) {
                // Reset form when dialog opens
                this.rating = 0;
                this.comment = '';
                this.commentTouched = false;
            }
        },
        rating() {
            if (!this.isCommentRequired) {
                this.commentTouched = false;
            }
        }
    },
    methods: {
        isStarFilled(star) {
            return star <= this.rating;
        },
        async submitFeedback() {
            if (this.rating === 0 || !this.classId) return;

            if (this.isCommentMissing) {
                this.commentTouched = true;
                return;
            }

            this.submitting = true;
            try {
                await window.axios.post(`/feedback/${this.classId}`, {
                    rating: this.rating,
                    comment: this.comment || null
                });
                this.$emit('feedback-submitted');
                this.$emit('input', false);
            } catch (error) {
                console.error('Failed to submit feedback:', error);
                this.$emit('feedback-error', 'Failed to submit feedback');
            } finally {
                this.submitting = false;
            }
        },
        skipFeedback() {
            // Just close the dialog without saving anything
            // The popup will show again on next page refresh
            this.$emit('feedback-skipped');
            this.$emit('input', false);
        }
    }
};
</script>

<style lang="scss" scoped>
.feedback-card {
    border-radius: 12px !important;
    overflow: visible;
    position: relative;
}

.feedback-header {
    display: flex;
    justify-content: flex-end;
    padding: 12px 12px 0 12px;
}

.close-btn {
    color: #999 !important;
}

.feedback-content {
    padding: 0 32px 32px 32px !important;
}

.feedback-title {
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 18px;
    color: #1E1E1E;
    margin-bottom: 8px;
    line-height: 1.4;
}

.feedback-subtitle {
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 14px;
    color: rgba(30, 30, 30, 0.6);
    margin-bottom: 24px;
}

.rating-container {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-bottom: 24px;
}

.star-btn {
    transition: transform 0.2s ease;

    &:hover {
        transform: scale(1.1);
    }
}

.comment-section {
    text-align: left;
    margin-bottom: 24px;
}

.comment-label {
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: #1E1E1E;
    display: block;
    margin-bottom: 8px;
}

.required-asterisk {
    color: #E53935;
    margin-left: 2px;
}

.comment-input {
    ::v-deep .v-input__slot {
        border-radius: 8px !important;
    }
}

.feedback-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}

.skip-btn {
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 14px;
    color: #666;
}

.submit-btn {
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 14px;
    padding: 0 24px !important;
    height: 40px !important;
    border-radius: 8px;
}
</style>

